import { getClient, authHeader } from './session'
import { getUrl } from './connection'
import { getRate } from './rate'
import { fetchJson } from './fetchData'

import { alert } from './stores/alert'
import { loading } from './stores/loading'

import { translate } from './i18n'

/**
 * 
 * @param {number|string} price to format or string if its secret
 * @returns {string} price formatted
 */
function formatPrice(price, customRate = null) {
    if (typeof price === 'string' || isNaN(price))
        return '****'
    const { separator = '.', symbol = '€', symbolPosition = 1, rate: clientRate = 1 } = customRate || this.rate;
    let result = ''
    if (symbolPosition === 0)
        result += symbol + ' '
    const rate = clientRate
    let priceDecimal = (price * rate).toFixed(2)
    const [integer, decimal] = priceDecimal.split('.')

    const thousands = /\B(?=(\d{3})+(?!\d))/g;
    result += integer.replace(thousands, separator)
    result += separator === '.' ? ',' : '.'
    result += decimal
    // result += price.toFixed(2).replace('.', separator)    
    if (symbolPosition === 1)
        result += ' ' + symbol
    return result
}

function getScenePrice(indexScene) {
    if (this.data.some(art => typeof art.price.finalPrice !== 'number'))
        return '****'
    return this.data.filter(r => r.sceneIndex === indexScene).reduce((acc, art) => acc + art.price.finalPrice, 0)
}

function getTotalPrice(order) {
    if (order.data.some(d => typeof d.price.finalPrice === 'string'))
        return '****'
    return order.data.reduce((acc, article) => acc + article.price.finalPrice, 0)
}

/**
 * 
 * @param {object} param0 
 * @param {string} [param0.typeOrder]
 * @param {string} [param0.queryOrder] 
 * @returns {Promise<Array<any>|null>}
 */
const getOrders = async ({ typeOrder = 'order', queryOrder = '', clientId = null }) => {
    clientId = clientId ?? getClient()
    if (!clientId) {
        return null;
    }

    let query = { clientId, type: typeOrder }
    if (queryOrder) {
        query[queryOrder.split('=')[0]] = queryOrder.split('=')[1];
    }

    const { data: rateClient } = await getRate();


    try {
        const orders = await fetchJson({ 
            endpoint: 'listorders', 
            params: query, 
            customErrorString: 'alerts.errors.notLoadOrders'
        });
        return orders.map(o => {
            // Ponemos la tarifa a cero para que no vuelva a aplicar el incremento al formatear el precio
            rateClient.rate = 1;
            rateClient.factoryPrice = 1;
            return { ...o, rate: rateClient, formatPrice }
        });
    }
    catch (err) {
        alert.newAlert({ message: translate('alerts.errors.notLoadOrders'), type: 'error' });
    }
    return null
}

const getOrderData = async ({ orderId }) => {
    let clientId = getClient();

    try {
        const order = await fetchJson({ 
            endpoint: 'orderdata',
            params: { orderId },
            customErrorString: 'alerts.errors.notLoadOrder404'
        });
        order.formatPrice = formatPrice
        order.getScenePrice = getScenePrice
        order.getTotalPrice = getTotalPrice(order)
        if (Number(clientId) !== order.client) {
            order.anonymousMode = true;
            //guardamos los datos que no queremos censurar en modo anónimo
            const remarks = order.contactData.remarks;
            //censuramos el resto de datos
            for (const key in order.contactData) {
                if (order.contactData[key] && order.contactData[key] !== "") {
                    order.contactData[key] = "****";
                }
            }
            order.shopName = "****";
            //recuperamos los datos que queremos que se vean en modo anónimo
            order.contactData.remarks = remarks;
        }
        if (!order.images) {
            order.images = order.image ? [order.image] : []
        }
        return order
    } catch (err) {
        console.error(err);
        if (err.code !== 404) {
            alert.newAlert({ message: translate('alerts.errors.notLoadOrder'), type: 'error' })
        }
        throw err
    }
}

const notifyOrder = async orderId => {
    const type = 'notifyorder';
    const [url, method] = await getUrl(type, { orderId });

    loading.newLoading();
    try {
        const response = await fetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
                ...authHeader()
            }
        });
        const result = await response.json()
        if (response.status !== 200) {
            console.warn(`Error in sending notification after order conversion ${response.status}: ${result}. Note that the order has been converted successfully, the error is only in the notification.`);
        }
        return result
    } catch (err) {
        alert.newAlert({ message: translate('alerts.errors.sendOrderConfirmation'), type: 'error' })
    }
    finally {
        loading.removeLoading();
    }
}

const budget2order = async idBudget => {
    const type = 'budget2order'
    const [url, method] = await getUrl(type, { idBudget });

    loading.newLoading()
    try {
        const response = await fetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
                ...authHeader()
            },
        })
        if (response.status === 200)
            alert.newAlert({ message: translate('alerts.info.budget2order'), type: 'successful' })
        else
            alert.newAlert({ message: translate((await response.json())['response']), type: 'error' })
        return await response.json()
    } catch (err) {
        alert.newAlert({ message: translate('alerts.errors.notConvertOrder'), type: 'error' })
    } finally {
        loading.removeLoading()
    }
}

const anulateBudget = async (budgetId) => {
    const type = 'anulatebudget'
    const [url, method] = await getUrl(type, { budgetId });

    loading.newLoading()
    try {
        const response = await fetch(url, {
            method,
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
                ...authHeader()
            },
        })
        if (response.status === 200)
            alert.newAlert({ message: translate('alerts.info.anulateBudget'), type: 'successful' })
        else
            alert.newAlert({ message: translate((await response.json())['response']), type: 'error' })
        return await response.json()
    } catch (err) {
        alert.newAlert({ message: translate('alerts.errors.notAnulateBudget'), type: 'error' })
    } finally {
        loading.removeLoading()
    }

}

/**
 * @enum {string}
 */
const orderStates = {
    UNKNOWN: 'UNKNOWN',
    BUDGET_DEFAULT: 'BUDGET_DEFAULT',
    BUDGET_NULL: 'BUDGET_NULL',
    BUDGET_PENDING: 'BUDGET_PENDING',
    BUDGET_VALIDATE: 'BUDGET_VALIDATE',
    BUDGET_CLOSE: 'BUDGET_CLOSE',
    BUDGET_TRANSFORMED: 'BUDGET_TRANSFORMED',
    ORDER_DEFAULT: 'ORDER_DEFAULT',
    ORDER_VALIDATE: 'ORDER_VALIDATE',
    ORDER_IN_PROGRESS: 'ORDER_IN_PROGRESS',
    ORDER_NULL: 'ORDER_NULL',
    ORDER_CLOSE: 'ORDER_CLOSE',
    ORDER_SERVED: 'ORDER_SERVED'
}

/**
 * 
 * @param {string} type of img to load
 * @returns {(e: Event) => void}
 */
const onErrorImg = type => e => {
    if (e && e.target && e.target instanceof Image) {
        if (e.target.src.match(/\/img\/defaults\//))
            e.target.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==' // default white pixel
        else
            e.target.src = `/img/defaults/${type}`
    }
}

const confirmBudget = async budgetId => {
    try {
        const result = await fetchJson({ 
            endpoint: 'confirmbudget', 
            params: { budgetId }, 
            customErrorString: 'alerts.errors.budgetConfirmationState'
        });
        return result;
    }
    catch (err) {
        alert.newAlert({ message: translate('alerts.errors.notLoadOrders'), type: 'error' });
    }
}

const unconfirmBudget = async budgetId => {
    try {
        const result = await fetchJson({ 
            endpoint: 'unconfirmbudget', 
            params: { budgetId }, 
            customErrorString: 'alerts.errors.budgetConfirmationState'
        });
        return result;
    }
    catch (err) {
        alert.newAlert({ message: translate('alerts.errors.notLoadOrders'), type: 'error' });
    }
}

export { getOrders, getOrderData, formatPrice, getScenePrice, notifyOrder, budget2order, orderStates, anulateBudget, onErrorImg, confirmBudget, unconfirmBudget }